import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useEffect,
} from "react";
import { UsersService } from "../../../services/user/users.ts";
import { ErrorResponse } from "../../../Models/Error.js";
import { User } from "../../../Models/User.js";
import { useAppContext } from "../../../AppProvider.jsx";
import {
  callSuccessToast,
  callErrorToast,
} from "../../../utilities/utilities.ts";

const usersService = new UsersService();

interface DeleteUsersProviderProps {
  children: ReactNode;
  data?: string[];
  callback?: () => void;
}

interface DeleteUserContextValue {
  loader: boolean;
  deleteUsers: () => void;
  data: string[];
  callback?: () => void;
}

const DeleteUserContext = createContext<DeleteUserContextValue | undefined>(
  undefined
);

const DeleteUserProvider: React.FC<DeleteUsersProviderProps> = ({
  children,
  data = [],
  callback,
}) => {
  const { dictionary } = useAppContext();
  const [error, setError] = useState<ErrorResponse<User> | null>(null);
  const [loader, setLoader] = useState<boolean>(false);

  const deleteUsers = (): void => {
    setLoader(true);
    const promises = data.map((id) => usersService.delete(id));
    Promise.all(promises)
      .then(() => {
        setLoader(false);
        if (callback) {
          callback();
        }
        callSuccessToast(dictionary.messages.save_successful);
      })
      .catch((err) => {
        callErrorToast(err);
        setLoader(false);
        setError(err);
      });
  };

  useEffect(() => {
    if (error) {
      callErrorToast(error);
    }
  }, [error]);

  return (
    <DeleteUserContext.Provider value={{ loader, deleteUsers, callback, data }}>
      {children}
    </DeleteUserContext.Provider>
  );
};

const useDeleteUserContext = (): DeleteUserContextValue => {
  const context = useContext(DeleteUserContext);
  if (!context) {
    throw new Error(
      "useDeleteUserContext must be used within a DeleteUserProvider"
    );
  }
  return context;
};

export { DeleteUserProvider, useDeleteUserContext };
